import { InputText } from "primereact/inputtext";
import extractDataFromMap from "../../utils/ExtractDataFromMap";
import { Password } from "primereact/password";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { format as dateFnsFormat, parse } from "date-fns";
import { Calendar } from "primereact/calendar";
import { InputSwitch } from "primereact/inputswitch";
import { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { MobileTimePicker } from "@mui/x-date-pickers/MobileTimePicker";
import assignDataToMap from "../../utils/AssignDataToMap";

const genElText = ({
  field,
  name,
  data,
  setData,
  disabled,
  width,
  autoFocus,
  error = {},
}) => {
  let value = data[field] ?? null;

  return (
    <div className={`col-start-start gap-1 ${width ?? "w-full"}`}>
      <label htmlFor={field} className="font-bold text-sm text-slate-500">
        {name}
      </label>
      <InputText
        id={field}
        disabled={disabled}
        className={`w-full ${error[field] ? "bg-red-100" : ""}`}
        autoFocus={autoFocus}
        value={value}
        onChange={(e) => {
          const newData = { ...data };
          assignDataToMap({ key: field, value: e.target.value, map: newData });
          setData(newData);
        }}
      />
      {error[field] && (
        <small className="font-semibold text-red-400">{error[field]}</small>
      )}
    </div>
  );
};

const genElNumber = ({
  field,
  name,
  data,
  setData,
  disabled,
  width,
  autoFocus,
  minFractionDigits,
  maxFractionDigits,
  min,
  max,
  showButtons,
  emptyWhenZero,
  placeholder,
  error = {},
}) => {
  // emptyWhenZero: 0 is shown as an empty input with "0" as the placeholder, and a cleared input is stored as 0
  let value = data[field] ?? 0;
  if (emptyWhenZero && !value) value = null;

  return (
    <div className={`col-start-start gap-1 min-w-0 ${width ?? "w-full"}`}>
      <label htmlFor={field} className="font-bold text-sm text-slate-500">
        {name}
      </label>
      <InputNumber
        id={field}
        className={`w-full min-w-0 max-w-full ${
          error[field] ? "bg-red-100" : ""
        }`}
        // the input may shrink below its default width (size=20), otherwise input + the two buttons are wider
        // than the card on a narrow screen
        inputClassName="min-w-0 !w-[1%]"
        autoFocus={autoFocus}
        value={value}
        disabled={disabled}
        locale="id-ID"
        placeholder={placeholder ?? (emptyWhenZero ? "0" : undefined)}
        showButtons={showButtons}
        // v on the left, ^ on the right of the input
        buttonLayout="horizontal"
        minFractionDigits={minFractionDigits ? minFractionDigits : 0}
        maxFractionDigits={maxFractionDigits ? maxFractionDigits : 0}
        min={min ? min : -999999999999}
        max={max ? max : 999999999999}
        // onChange={(e) => {
        //   const newData = { ...data };
        //   assignDataToMap({ key: field, value: e.value, map: newData });
        //   setData(newData);
        // }}
        onValueChange={(e) => {
          const newValue = emptyWhenZero ? e.value ?? 0 : e.value;
          // the empty input of a 0 value reports null, which is no change
          if (emptyWhenZero && newValue === (data[field] ?? 0)) return;
          const newData = { ...data };
          assignDataToMap({ key: field, value: newValue, map: newData });
          setData(newData);
        }}
        incrementButtonIcon="pi pi-angle-up"
        decrementButtonIcon="pi pi-angle-down"
        pt={
          showButtons
            ? undefined
            : {
                incrementButton: { className: "max-w-[1.5rem]" },
                decrementButton: { className: "max-w-[1.5rem]" },
              }
        }
      />
      {error[field] && (
        <small className="font-semibold text-red-400">{error[field]}</small>
      )}
    </div>
  );
};

const genElTime = ({
  field,
  name,
  data,
  setData,
  disabled,
  width,
  autoFocus,
  error = {},
}) => {
  let value = data[field] ?? null;

  return (
    <div className={`col-start-start gap-1 ${width ?? "w-full"}`}>
      <label htmlFor={field} className="font-bold text-sm text-slate-500">
        {name}
      </label>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <MobileTimePicker
          disabled={disabled}
          className={`w-full ${error[field] ? "bg-red-100" : ""}`}
          value={value}
          ampm={false}
          onChange={(e) => {
            const newData = { ...data };
            assignDataToMap({ key: field, value: e, map: newData });
            setData(newData);
          }}
        />
      </LocalizationProvider>
      {error[field] && (
        <small className="font-semibold text-red-400">{error[field]}</small>
      )}
    </div>
  );
};

const genElDate = ({
  field,
  name,
  data,
  setData,
  primeFormat,
  disabled,
  width,
  showTime,
  autoFocus,
  error = {},
}) => {
  let value = data && data[field] ? data[field] : null;

  return (
    <div className={`col-start-start gap-1 ${width ?? "w-full"}`}>
      <label htmlFor={field} className="font-bold text-sm text-slate-500">
        {name}
      </label>
      <Calendar
        className={`w-full ${error[field] ? "bg-red-100" : ""}`}
        id={field}
        disabled={disabled}
        autoFocus={autoFocus}
        value={value ?? null}
        showIcon
        showButtonBar
        showTime={showTime}
        dateFormat={primeFormat ?? "dd/mm/yy"}
        hourFormat="24"
        onChange={(e) => {
          const newData = { ...data };
          assignDataToMap({ key: field, value: e.value, map: newData });
          setData(newData);
        }}
        locale="id"
      />
      {error[field] && (
        <small className="font-semibold text-red-400">{error[field]}</small>
      )}
    </div>
  );
};

const genElDateRange = ({
  field,
  name,
  data,
  setData,
  primeFormat,
  disabled,
  width,
  autoFocus,
  error = {},
}) => {
  let value = data[field] ?? null;

  return (
    <div className={`col-start-start gap-1 ${width ?? "w-full"}`}>
      <label htmlFor={field} className="font-bold text-sm text-slate-500">
        {name}
      </label>
      <Calendar
        className={`w-full ${error[field] ? "bg-red-100" : ""}`}
        id={field}
        disabled={disabled}
        autoFocus={autoFocus}
        value={value ?? null}
        selectionMode="range"
        hideOnRangeSelection
        showIcon
        showButtonBar
        dateFormat={primeFormat ?? "dd/mm/yy"}
        hourFormat="24"
        onChange={(e) => {
          const newData = { ...data };
          assignDataToMap({ key: field, value: e.value, map: newData });
          setData(newData);
        }}
        locale="id"
      />
      {error[field] && (
        <small className="font-semibold text-red-400">{error[field]}</small>
      )}
    </div>
  );
};

const genElObject = ({
  name,
  field,
  value,
  onClick,
  onClear,
  disabled,
  autoFocus,
  width,
  error = {},
}) => {
  return (
    <div className={`col-start-start gap-1 ${width ?? "w-full"}`}>
      <label htmlFor={field} className="font-bold text-sm text-slate-500">
        {name}
      </label>
      <div className="row-start-center gap-1 w-full">
        <Button
          disabled={disabled}
          className={`bg-indigo-50 border-indigo-100 w-full text-slate-700 leading-tight ${
            error[field] ? "bg-red-100" : ""
          }`}
          outlined
          onClick={(e) => {
            e.preventDefault();
            if (onClick) {
              onClick();
            }
          }}
        >
          <div className="row-between-center w-100">
            {!value || value.length < 0 ? "-" : value}
            <i className="pi pi-search" />
          </div>
        </Button>
        {onClear && (
          <Button
            className="bg-red-400 border-red-400 max-w-[1.8rem] max-h-[1.8rem]"
            icon="pi pi-times"
            rounded
            severity="danger"
            onClick={(e) => {
              e.preventDefault();
              if (onClear) {
                onClear();
              }
            }}
          />
        )}
      </div>
      {error[field] && (
        <small className="font-semibold text-red-400">{error[field]}</small>
      )}
    </div>
  );
};

export {
  genElDateRange,
  genElObject,
  genElDate,
  genElText,
  genElNumber,
  genElTime,
};
